<template>
    <toolbar-option type="stacked" validation-message="Alt is required">
        <template slot="primary">
            <div class="image-control">
                <button
                    v-if="hasImage"
                    class="image-control__remove"
                    @click="removeImage"
                >
                    <font-awesome-icon class="image-control__remove-icon" :icon="['fal', 'trash-alt']" />
                    Remove Image
                </button>

                <div class="image-control__text">
                    <span class="image-control__label">
                        File Name:
                    </span>

                    <span class="image-control__input">
                        {{ filename }}
                    </span>
                </div>
            </div>
            <div class="image-control__browse">
                <button class="image-control__button" @click="openMediaLibrary">
                    Browse
                </button>
                <button v-if="hasDAM" class="image-control__button" @click="openDAM">
                    Browse DAM
                </button>
            </div>
        </template>
    </toolbar-option>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import uniqid from 'uniqid';

import baseOption from '@/options/mixins/base-option';
import { SET_CHANGED_DATA, SET_SELECTED_MEDIA_TARGET, SHOW_DRAWER } from '@/store/types/mutation-types';
import mediaTargets from '@/data/constants/media-targets';
import { drawerHelperMixin, TYPE_IMAGE } from '@/mixins/drawer-helper';

export default {
    name: 'image-selector',

    mixins: [baseOption, drawerHelperMixin],

    data() {
        return {
            displayName: 'Image',
            id: uniqid(),
            filename: 'N/A',
        };
    },

    computed: {
        ...mapState({
            selectedMedia: state => state.page.selectedMedia,
            selectedMediaTarget: state => state.page.selectedMediaTarget,
        }),

        hasDAM() {
            return process.env.VUE_APP_CH && process.env.VUE_APP_CH !== '#{ch_server}#';
        },

        hasImage() {
            const image = this.selectedElements[0].srcNew;
            const img = this.selectedElements[0].$el.querySelector('img');
            let imgSrc = '';
            if (img) {
                imgSrc = img.getAttribute('src');
            }

            return image || (imgSrc && imgSrc !== 'undefined');
        },
    },

    watch: {
        selectedMedia() {
            if (this.selectedMediaTarget !== mediaTargets.IMAGE) return;

            this.selectedElements[0].srcNew = this.selectedMedia.url;
            this.selectedElements[0].altNew = this.selectedMedia.altText;
            this.filename = this.getFileName(this.selectedMedia.url);
            this.setChangedData(true);
            this.onOptionChange(this.selectedMedia.url);
        },
    },

    methods: {
        ...mapMutations({
            setChangedData: SET_CHANGED_DATA,
            setSelectedMediaTarget: SET_SELECTED_MEDIA_TARGET,
            showDrawer: SHOW_DRAWER,
        }),

        onSelectedElement() {
            const image = this.selectedElements[0].srcNew;

            this.filename = this.getFileName(image) || 'N/A';
        },

        getFileName(src) {
            if (!src) return '';

            let filename = src.slice(src.lastIndexOf('/') + 1);
            const queryStringIndex = filename.indexOf('?');
            const maxLength = 15;

            if (queryStringIndex > -1) filename = filename.slice(0, queryStringIndex);
            if (filename.length > maxLength) filename = `...${ filename.slice(filename.length - maxLength) }`;

            return filename;
        },

        openMediaLibrary() {
            this.openMediaDrawer(TYPE_IMAGE);
            this.setSelectedMediaTarget(mediaTargets.IMAGE);
        },

        openDAM() {
            this.showDrawer('content-hub-drawer');
            this.setSelectedMediaTarget(mediaTargets.IMAGE);
        },

        previewFile(event) {
            const img = window.URL.createObjectURL(event.target.files[0]);

            this.selectedElements[0].$el.querySelector('img').src = img;
        },

        removeImage() {
            if (!this.selectedElements[0].srcNew) {
                this.selectedElements[0].srcNew = 'a'; // force computed to check image
            }

            this.selectedElements[0].removeImage();
            this.selectedElements[0].$el.querySelector('img').src = '';
            this.filename = 'N/A';
            this.setChangedData(true);
            this.onOptionChange();
        },
    },
};
</script>

<style lang="scss" scoped>
.image-control {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    position: relative;

    &__remove {
        position: absolute;
        top: -1.75rem;
        right: 0;
        font-size: 11px;
        color: var(--form-deleteLinkColor);

        &:hover {
            color: var(--form-deleteLinkHoverColor);
        }
    }

    &__remove-icon {
        margin-right: 3px;
    }

    &__label {
        display: block;
        font-size: 12px;
        margin-top: 5px;
    }

    &__input {
        line-height: 1.4;
        width: 100%;
        font-size: 14px;
    }

    &__button {
        @extend %button;
        font-size: 13px;
        padding: 6px 24px;
        min-width: inherit;
    }

    &__file {
        position: absolute;
        z-index: -1;
        opacity: 0;
    }

    &__browse {
        width: 100%;
        margin-top: 10px;

        button {
            margin-right: 15px;
        }
    }
}
</style>
